/**
 * FERP Portal - Scope Checking Hook
 *
 * Provides `useHasScope` for checking if the current user has specific scopes.
 * DRR-313: Uses NavigationContext (single query) instead of direct GraphQL calls.
 *
 * Usage:
 *   const { hasScope, loading } = useScopeCheck();
 *   if (hasScope('facility:admin')) { ... }
 *
 * Or for simple boolean check:
 *   const canEdit = useHasScope('task:write');
 */

import { useMemo } from 'react';
import { useNavigation } from '@/contexts/NavigationContext';

/**
 * Hook that provides scope checking capabilities.
 * DRR-313: Uses shared NavigationContext instead of duplicate query.
 */
export function useScopeCheck(): {
  hasScope: (scope: string) => boolean;
  hasScopeAny: (scopes: string[]) => boolean;
  hasScopeAll: (scopes: string[]) => boolean;
  scopes: string[];
  loading: boolean;
} {
  const { userScopes, hasScope: ctxHasScope, hasAnyScope, loading } = useNavigation();

  const scopes = useMemo(() => Array.from(userScopes), [userScopes]);

  const hasScopeAll = useMemo(
    () => (checkScopes: string[]) => checkScopes.every((s) => userScopes.has(s)),
    [userScopes]
  );

  return {
    hasScope: ctxHasScope,
    hasScopeAny: hasAnyScope,
    hasScopeAll,
    scopes,
    loading,
  };
}

/**
 * Simple hook for checking a single scope.
 * Returns false while loading (safe default).
 */
export function useHasScope(scope: string): boolean {
  const { hasScope, loading } = useScopeCheck();
  // Return false while loading to prevent flash of unauthorized content
  return !loading && hasScope(scope);
}

/**
 * Hook for checking if user has ANY of the provided scopes.
 */
export function useHasScopeAny(scopes: string[]): boolean {
  const { hasScopeAny, loading } = useScopeCheck();
  return !loading && hasScopeAny(scopes);
}

/**
 * Hook for checking if user has ALL of the provided scopes.
 */
export function useHasScopeAll(scopes: string[]): boolean {
  const { hasScopeAll, loading } = useScopeCheck();
  return !loading && hasScopeAll(scopes);
}
